import React, { useState, useEffect } from "react";
import { authService } from "../services/authService";

const BusinessProfileEdit = ({ user, onProfileUpdate, onCancel }) => {
  const [formData, setFormData] = useState({
    businessName: "",
    businessType: "",
    businessDescription: "",
    businessPhone: "",
    website: "",
    businessAddress: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
    },
    contactPerson: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    businessRegistrationNumber: "",
    taxId: "",
    operatingHours: {
      monday: { open: "09:00", close: "17:00", closed: false },
      tuesday: { open: "09:00", close: "17:00", closed: false },
      wednesday: { open: "09:00", close: "17:00", closed: false },
      thursday: { open: "09:00", close: "17:00", closed: false },
      friday: { open: "09:00", close: "17:00", closed: false },
      saturday: { open: "09:00", close: "17:00", closed: false },
      sunday: { open: "09:00", close: "17:00", closed: true },
    },
    preferences: {
      notifications: true,
      newsletter: false,
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const businessTypes = [
    "Restaurant",
    "Grocery Store",
    "Retail",
    "Service Provider",
    "Healthcare",
    "Education",
    "Technology",
    "Manufacturing",
    "Other",
  ];

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        businessName: user.businessName || "",
        businessType: user.businessType || "",
        businessDescription: user.businessDescription || "",
        businessPhone: user.businessPhone || "",
        website: user.website || "",
        businessAddress: {
          street: user.businessAddress?.street || "",
          city: user.businessAddress?.city || "",
          state: user.businessAddress?.state || "",
          zipCode: user.businessAddress?.zipCode || "",
        },
        contactPerson: {
          firstName: user.contactPerson?.firstName || "",
          lastName: user.contactPerson?.lastName || "",
          email: user.contactPerson?.email || "",
          phone: user.contactPerson?.phone || "",
        },
        businessRegistrationNumber: user.businessRegistrationNumber || "",
        taxId: user.taxId || "",
        operatingHours: {
          monday: user.operatingHours?.monday || {
            open: "09:00",
            close: "17:00",
            closed: false,
          },
          tuesday: user.operatingHours?.tuesday || {
            open: "09:00",
            close: "17:00",
            closed: false,
          },
          wednesday: user.operatingHours?.wednesday || {
            open: "09:00",
            close: "17:00",
            closed: false,
          },
          thursday: user.operatingHours?.thursday || {
            open: "09:00",
            close: "17:00",
            closed: false,
          },
          friday: user.operatingHours?.friday || {
            open: "09:00",
            close: "17:00",
            closed: false,
          },
          saturday: user.operatingHours?.saturday || {
            open: "09:00",
            close: "17:00",
            closed: false,
          },
          sunday: user.operatingHours?.sunday || {
            open: "09:00",
            close: "17:00",
            closed: true,
          },
        },
        preferences: {
          notifications: user.preferences?.notifications ?? true,
          newsletter: user.preferences?.newsletter ?? false,
        },
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleOperatingHoursChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await authService.updateBusinessProfile(formData);
      setSuccess("Business profile updated successfully!");

      // Update the user data in the parent component
      if (onProfileUpdate) {
        onProfileUpdate(response.business);
      }

      // Auto-close after 2 seconds
      setTimeout(() => {
        onCancel();
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to update business profile");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-edit-overlay">
      <div className="profile-edit-modal business-profile-modal">
        <div className="modal-header">
          <h2>Edit Business Profile</h2>
          <button className="close-button" onClick={onCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="profile-edit-form">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-section">
            <h3>Business Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Business Name</label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Business Type</label>
                <select
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Business Type</option>
                  {businessTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Business Description</label>
              <textarea
                name="businessDescription"
                value={formData.businessDescription}
                onChange={handleInputChange}
                rows="3"
                placeholder="Brief description of your business..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Business Phone</label>
                <input
                  type="tel"
                  name="businessPhone"
                  value={formData.businessPhone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://www.yourbusiness.com"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Business Address</h3>
            <div className="form-group">
              <label>Street Address</label>
              <input
                type="text"
                name="businessAddress.street"
                value={formData.businessAddress.street}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="businessAddress.city"
                  value={formData.businessAddress.city}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  name="businessAddress.state"
                  value={formData.businessAddress.state}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  name="businessAddress.zipCode"
                  value={formData.businessAddress.zipCode}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Contact Person</h3>
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="contactPerson.firstName"
                  value={formData.contactPerson.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="contactPerson.lastName"
                  value={formData.contactPerson.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Contact Email</label>
                <input
                  type="email"
                  name="contactPerson.email"
                  value={formData.contactPerson.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contact Phone</label>
                <input
                  type="tel"
                  name="contactPerson.phone"
                  value={formData.contactPerson.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Business Registration</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Business Registration Number</label>
                <input
                  type="text"
                  name="businessRegistrationNumber"
                  value={formData.businessRegistrationNumber}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label>Tax ID</label>
                <input
                  type="text"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Operating Hours</h3>
            <div className="operating-hours-grid">
              {days.map((day) => (
                <div key={day} className="operating-hours-day">
                  <div className="day-header">
                    <span className="day-name">
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </span>
                    <label className="closed-toggle">
                      <input
                        type="checkbox"
                        checked={formData.operatingHours[day].closed}
                        onChange={(e) =>
                          handleOperatingHoursChange(
                            day,
                            "closed",
                            e.target.checked
                          )
                        }
                      />
                      Closed
                    </label>
                  </div>
                  {!formData.operatingHours[day].closed && (
                    <div className="time-inputs">
                      <input
                        type="time"
                        value={formData.operatingHours[day].open}
                        onChange={(e) =>
                          handleOperatingHoursChange(
                            day,
                            "open",
                            e.target.value
                          )
                        }
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={formData.operatingHours[day].close}
                        onChange={(e) =>
                          handleOperatingHoursChange(
                            day,
                            "close",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>Preferences</h3>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="preferences.notifications"
                  checked={formData.preferences.notifications}
                  onChange={handleInputChange}
                />
                Receive notifications
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="preferences.newsletter"
                  checked={formData.preferences.newsletter}
                  onChange={handleInputChange}
                />
                Subscribe to newsletter
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessProfileEdit;
